import React from "react";
import {
  Box,
  IconButton,
  Typography,
  Tabs,
  Tab,
  Stack
} from "@mui/material";
import { CloseIcon, PersonIcon, SecurityIcon, SettingsIcon } from "../../icons";
import { observer } from "mobx-react-lite";
import { useRootStore } from "../../store/contexts";
import { ProfileSettingsPage } from "./ProfileSettingsPage";
import { SecuritySettingsPage } from "./SecuritySettingsPage";
import { PreferencesSettingsPage } from "./PreferencesSettingsPage";

export type SettingsSection = "profile" | "security" | "preferences";

export type SettingsDrawerProps = {
  section: SettingsSection;
  onClose: () => void;
  onSectionChange: (section: SettingsSection) => void;
};

export const SettingsDrawer = observer(({
  section,
  onClose,
  onSectionChange
}: SettingsDrawerProps) => {
  const rootStore = useRootStore();
  const { langStore } = rootStore;

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            {langStore.settingsLabel}
          </Typography>
          <IconButton onClick={onClose} edge="end">
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <Tabs
        value={section}
        onChange={(_event, newValue: SettingsSection) => onSectionChange(newValue)}
        variant="fullWidth"
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab
          value="profile"
          label={langStore.profileSettingsLabel}
          icon={<PersonIcon />}
          iconPosition="start"
          sx={{ minHeight: 64 }}
        />
        <Tab
          value="security"
          label={langStore.securitySettingsLabel}
          icon={<SecurityIcon />}
          iconPosition="start"
          sx={{ minHeight: 64 }}
        />
        <Tab
          value="preferences"
          label={langStore.preferencesSettingsLabel}
          icon={<SettingsIcon />}
          iconPosition="start"
          sx={{ minHeight: 64 }}
        />
      </Tabs>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 3,
        }}
      >
        {section === 'profile' && <ProfileSettingsPage />}
        {section === 'security' && <SecuritySettingsPage />}
        {section === 'preferences' && <PreferencesSettingsPage />}
      </Box>
    </Box>
  );
});
